import React, { Component } from "react";
import Header from "../components/Header/Header";

import { BrowserRouter as Router, Route, Redirect, Switch, useLocation } from "react-router-dom";

import { withUserContext } from "./hoc/withUserContext";
import ErrorBoundary from "./common/ErrorBoundary";
import AsyncComponent from "./common/AsyncComponent";
import Competition from "./pages/Competition/Competition";
import CompetitionTournaments from "./pages/Competition/CompetitionTournaments";
import EditCompetition from "./pages/Competition/EditCompetition";
import CompetitionAdmins from "./pages/Competition/CompetitionAdmins";
import CompetitionEstablishments from "./pages/Competition/CompetitionEstablishments";
import StatsBySport from "./pages/Stats/StatsBySport";
import ClassificationsIndex from "./pages/Classification/ClassificationRowsIndex";
import CreateClassification from "./pages/Classification/CreateClassification";
import PunctuationIndex from "./pages/Classification/PunctuationIndex";
import CreatePunctuationSystem from "./pages/Classification/CreatePunctuationSystem";
import ClassificationEventsIndex from "./pages/Classification/ClassificationEventsIndex";
import CreateClassificationEvent from "./pages/Classification/CreateClassificationEvent";
import RankingImage from "./pages/Classification/RankingImage";
import MembersIndexPage from "./pages/Organization/Members/MembersIndexPage";
import OrganizationMembershipSettingsPage from "./pages/Organization/Members/OrganizationMembershipSettingsPage";
import OrganizationLanding from "./pages/Organization/OrganizationLanding";
import ErrorPage from "./pages/ErrorPages/404";
import EventDetail from "./pages/Competition/EventDetails";
import CompetitionDrawer from "./pages/Competition/CompetitionDrawer";
import OrganizationDrawer from "./pages/Classification/OrganizationDrawer";
import OrganizationDashboard from "./pages/Organization/OrganizationDashboard";
import PunctuationSystemShow from "./pages/Classification/PunctuationSystemShow";
import { ROUTES } from "constants/routes";
import OrganizationSettingsForm from "./pages/Organization/OrganizationSettingsForm";
import CreateTournamentPage, { GestionCreateTournamentPage } from "./pages/Tournament/CreateTournamentPage";
import UnlinkedPlayersPage from "./pages/Organization/Members/UnlinkedPlayersPage";
import CompetitionFixture from "./pages/Competition/Fixture/CompetitionFixture";
import CompetitionScheduling from "./pages/Competition/CompetitionScheduling";
import CompetitionCustomFields from "./pages/Competition/CompetitionCustomFields";
import GestionDashboard from "./pages/Gestion/GestionDashboard";
import GestionTournaments from "./pages/Gestion/GestionTournaments";
import GestionSettings from "./pages/Gestion/GestionSettings";
import GestionTeams from "./pages/Gestion/GestionTeams";
import GestionTeamDetail from "./pages/Gestion/GestionTeamDetail";
import GestionPlayers from "./pages/Gestion/GestionPlayers";
import GestionMatches from "./pages/Gestion/GestionMatches";
import GestionBranding from "./pages/Gestion/GestionBranding";
import GestionFinancials from "./pages/Gestion/GestionFinancials";
import GestionAccountFinancePage from "./pages/Gestion/GestionAccountFinancePage";
import GestionPaymentDetailPage from "./pages/Gestion/GestionPaymentDetailPage";
import GestionPushBroadcasts from "./pages/Gestion/GestionPushBroadcasts";
import { PUSH_AREA } from "./pages/Gestion/push-broadcast-helpers";
import GestionMigrationWizard from "./pages/Gestion/GestionMigrationWizard";
import GestionCustomerRedirect from "./pages/Gestion/GestionCustomerRedirect";
import GestionAreaGuard from "./pages/Gestion/GestionAreaGuard";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { GESTION_COMPETITION_BASE, renderGestionCompetitionRoute } from "./pages/Gestion/renderGestionCompetitionRoute";
import { renderGestionPage } from "./pages/Gestion/renderGestionPage";
import CompetitionMatchScoring from "./pages/Competition/CompetitionMatchScoring";
import CompetitionRegistrationsPage from "./pages/Competition/CompetitionRegistrationsPage";
import UserContext from "../context/UserContext";

const AsyncHome = AsyncComponent(() => import("./pages/Home/Home"));
const AsyncTournament = AsyncComponent(() => import("./pages/Tournament/Tournament"));
const AsyncLeaguePlanilleroPage = AsyncComponent(() => import("./pages/Tournament/League/LeaguePlanilleroPage"));
const AsyncPageWithDrawer = AsyncComponent(() => import("./pages/PageWithDrawer"));
const AsyncCreateCompetitionWizard = AsyncComponent(() => import("./pages/Competition/CreateCompetitionWizard"));

const AsyncGeneralError = AsyncComponent(() => import("./pages/ErrorPages/500"));
const AsyncNotFound = AsyncComponent(() => import("./pages/ErrorPages/404"));
const AsyncSingIn = AsyncComponent(() => import("./pages/SignIn/SignInSide"));
const AsyncSignUp = AsyncComponent(() => import("./pages/SignIn/SignUp"));
const AsyncForgotPassword = AsyncComponent(() => import("./pages/SignIn/ForgotPassword"));
const AsyncLanding = AsyncComponent(() => import("./pages/Landing"));
const AsyncRankingList = AsyncComponent(() => import("./pages/Classification/RankingsList"));
const AsyncRanking = AsyncComponent(() => import("./pages/Classification/Ranking"));
const AsyncClassificationShow = AsyncComponent(() => import("./pages/Classification/ClassificationShow"));
const AsyncMemberProfilePage = AsyncComponent(() => import("./pages/Organization/Members/MemberProfilePage"));
const AsyncClubBoard = AsyncComponent(() => import("./pages/ClubBoard/ClubBoard"));

const CompetitionRouteLayout = ({ content, ...props }) => (
  <ErrorBoundary key={props.match.url} {...props}>
    <AsyncPageWithDrawer
      {...props}
      drawerComponent={<CompetitionDrawer />}
      enableDrawerCollapse
      content={content}
    />
  </ErrorBoundary>
);

const createCompetitionRoute = (Content, contentProps = {}) => {
  const CompetitionRoute = (props) => {
    const userContext = React.useContext(UserContext);

    return (
      <CompetitionRouteLayout
        {...props}
        content={<Content userContext={userContext} {...contentProps} {...props} />}
      />
    );
  };

  return CompetitionRoute;
};

const createBoundedRoute = (Content, extraProps = {}) => {
  const BoundedRoute = (props) => {
    const userContext = React.useContext(UserContext);
    return (
      <ErrorBoundary {...props}>
        <Content userContext={userContext} {...extraProps} {...props} />
      </ErrorBoundary>
    );
  };

  return BoundedRoute;
};

const createOrganizationRoute = (Content, extraProps = {}) => {
  const OrganizationRoute = (props) => {
    const userContext = React.useContext(UserContext);
    return (
      <ErrorBoundary {...props}>
        <AsyncPageWithDrawer
          {...props}
          drawerComponent={<OrganizationDrawer />}
          content={<Content userContext={userContext} {...extraProps} {...props} />}
        />
      </ErrorBoundary>
    );
  };

  return OrganizationRoute;
};

const redirectToGestionTournaments = (props) => (
  <Redirect to={GESTION_ROUTES.tournaments(props.match.params.customerId)} />
);
const redirectToGestionPlayers = (props) => (
  <Redirect to={GESTION_ROUTES.players(props.match.params.customerId)} />
);
const redirectToGestionMembershipSettings = (props) => (
  <Redirect to={GESTION_ROUTES.membershipSettings(props.match.params.customerId)} />
);
const redirectToGestionSettings = (props) => (
  <Redirect to={GESTION_ROUTES.settings(props.match.params.customerId)} />
);
const redirectToGestionSettingsApp = (props) => (
  <Redirect to={GESTION_ROUTES.settingsApp(props.match.params.customerId)} />
);
const redirectToDashboard = () => <Redirect to={ROUTES.ADMIN.DASHBOARD} />;

const CompetitionDashboardRoute = createCompetitionRoute(Competition);
const CompetitionClubsRoute = createCompetitionRoute(CompetitionEstablishments);
const CompetitionManagersRoute = createCompetitionRoute(CompetitionAdmins);
const CompetitionTournamentsRoute = createCompetitionRoute(CompetitionTournaments);
const CompetitionTournamentRoute = createCompetitionRoute(AsyncTournament, { adminMode: true });
const CompetitionRegistrationsRoute = createCompetitionRoute(CompetitionRegistrationsPage);
const CompetitionEditRoute = createCompetitionRoute(EditCompetition);
const CompetitionSchedulingRoute = createCompetitionRoute(CompetitionScheduling);
const CompetitionScheduleRoute = createCompetitionRoute(CompetitionFixture);
const CompetitionCustomFieldsRoute = createCompetitionRoute(CompetitionCustomFields);
const CompetitionMatchScoringRoute = createCompetitionRoute(CompetitionMatchScoring);

const CreateCompetitionTournamentRoute = (props) => (
  <ErrorBoundary key={props.match.url} {...props}>
    <CreateTournamentPage {...props} competitionUUID={props.match.params.id} />
  </ErrorBoundary>
);

const GestionCreateCompetitionTournamentRoute = (props) => (
  <ErrorBoundary {...props}>
    <GestionAreaGuard area="tournaments">
      <CreateTournamentPage {...props} competitionUUID={props.match.params.id} useGestionShell />
    </GestionAreaGuard>
  </ErrorBoundary>
);

const LandingRoute = createBoundedRoute(AsyncLanding);
const SignInRoute = createBoundedRoute(AsyncSingIn);
const SignUpRoute = createBoundedRoute(AsyncSignUp);
const ForgotPasswordRoute = createBoundedRoute(AsyncForgotPassword);
const PublicPlanilleroRoute = createBoundedRoute(AsyncLeaguePlanilleroPage);
const PublicTournamentRoute = createBoundedRoute(AsyncTournament);
const PublicRankingsRoute = createBoundedRoute(AsyncRankingList);
const PublicRankingRoute = createBoundedRoute(AsyncRanking);
const PublicRankingImageRoute = createBoundedRoute(RankingImage);
const PublicMemberProfileRoute = createBoundedRoute(AsyncMemberProfilePage);
const PublicOrganizationLandingRoute = createBoundedRoute(OrganizationLanding);
const PublicEventDetailRoute = createBoundedRoute(EventDetail);
const HomeRoute = createBoundedRoute(AsyncHome);
const CreateCompetitionWizardRoute = createBoundedRoute(AsyncCreateCompetitionWizard);
const GestionCustomerRedirectRoute = createBoundedRoute(GestionCustomerRedirect);
const GestionCreateTournamentRoute = createBoundedRoute((props) => (
  <GestionAreaGuard area="tournaments">
    <GestionCreateTournamentPage {...props} />
  </GestionAreaGuard>
));
const StatsBySportRoute = createBoundedRoute(StatsBySport);
const GeneralErrorRoute = createBoundedRoute(AsyncGeneralError);
const NotFoundRoute = createBoundedRoute(AsyncNotFound);

const OrganizationDashboardRoute = createOrganizationRoute(OrganizationDashboard);
const OrganizationMembershipSettingsRoute = createOrganizationRoute(OrganizationMembershipSettingsPage);
const OrganizationMembersRoute = createOrganizationRoute(MembersIndexPage);
const OrganizationUnlinkedPlayersRoute = createOrganizationRoute(UnlinkedPlayersPage);
const OrganizationSettingsRoute = createOrganizationRoute(OrganizationSettingsForm);
const OrganizationClassificationsRoute = createOrganizationRoute(ClassificationsIndex, { archived: false });
const OrganizationCreateClassificationRoute = createOrganizationRoute(CreateClassification);
const OrganizationArchivedClassificationsRoute = createOrganizationRoute(ClassificationsIndex, { archived: true });
const OrganizationClassificationShowRoute = createOrganizationRoute(AsyncClassificationShow);
const OrganizationPunctuationsRoute = createOrganizationRoute(PunctuationIndex);
const OrganizationCreatePunctuationRoute = createOrganizationRoute(CreatePunctuationSystem);
const OrganizationPunctuationShowRoute = createOrganizationRoute(PunctuationSystemShow);
const OrganizationClassificationEventsRoute = createOrganizationRoute(ClassificationEventsIndex);
const OrganizationCreateClassificationEventRoute = createOrganizationRoute(CreateClassificationEvent);
const OrganizationEditClassificationEventRoute = createOrganizationRoute(CreateClassificationEvent);

const GestionDashboardRoute = renderGestionPage(GestionDashboard);
const GestionTournamentsRoute = renderGestionPage(GestionTournaments, { area: "tournaments" });
const GestionTournamentRoute = renderGestionPage((props) => <AsyncTournament {...props} adminMode />, {
  area: "tournaments",
});
const GestionTeamDetailRoute = renderGestionPage(GestionTeamDetail, { area: "tournaments" });
const GestionTeamsRoute = renderGestionPage(GestionTeams, { area: "tournaments" });
const GestionPlayersRoute = renderGestionPage(GestionPlayers, { area: "tournaments" });
const GestionMatchesRoute = renderGestionPage(GestionMatches, { area: "tournaments" });
const GestionBrandingRoute = renderGestionPage(GestionBranding, { area: "manager" });
const GestionSettingsRoute = renderGestionPage(GestionSettings, { area: "tournaments" });
const GestionFinancialsRoute = renderGestionPage(GestionFinancials, { area: "finance" });
const GestionAccountFinanceRoute = renderGestionPage(GestionAccountFinancePage, { area: "finance" });
const GestionPaymentDetailRoute = renderGestionPage(GestionPaymentDetailPage, { area: "finance" });
const GestionPushBroadcastsRoute = renderGestionPage(GestionPushBroadcasts, { area: PUSH_AREA });
const GestionMigrationRoute = renderGestionPage(GestionMigrationWizard, { area: "reva-admin" });

const GestionCompetitionCustomFieldsRoute = renderGestionCompetitionRoute(CompetitionCustomFields, {
  area: "tournaments",
});
const GestionCompetitionSchedulingRoute = renderGestionCompetitionRoute(CompetitionScheduling, { area: "tournaments" });
const GestionCompetitionScheduleRoute = renderGestionCompetitionRoute(CompetitionFixture, { area: "tournaments" });
const GestionCompetitionEditRoute = renderGestionCompetitionRoute(EditCompetition, { area: "manager" });
const GestionCompetitionClubsRoute = renderGestionCompetitionRoute(CompetitionEstablishments, { area: "tournaments" });
const GestionCompetitionManagersRoute = renderGestionCompetitionRoute(CompetitionAdmins, { area: "manager" });

const AdminRouteGroup = ({ isLoggedIn, children }) => {
  const location = useLocation();

  if (!isLoggedIn) {
    return <Redirect to={{ pathname: "/signin", state: { from: location } }} />;
  }

  return <Switch>{children}</Switch>;
};

class AppRouter extends Component {
  render() {
    const { userContext } = this.props;
    return (
      <React.Fragment>
        {userContext.userReady && (
          <Router>
            <div>
              {this.props.children}
              <Header {...this.props} />
              <Switch>
                <Redirect exact from="/" to={ROUTES.ADMIN.DASHBOARD} />
                <Route path={ROUTES.PUBLIC.LANDING} component={LandingRoute} />
                <Route exact path={ROUTES.PUBLIC.SIGNIN} component={SignInRoute} />
                <Route exact path={ROUTES.PUBLIC.REGISTER} component={SignUpRoute} />
                <Route exact path={ROUTES.PUBLIC.FORGOT_PASSWORD} component={ForgotPasswordRoute} />
                <Route
                  exact
                  path={`${ROUTES.PUBLIC.TOURNAMENT}/:id/planillero/:matchId`}
                  component={PublicPlanilleroRoute}
                />
                <Route path={`${ROUTES.PUBLIC.TOURNAMENT}/:id`} component={PublicTournamentRoute} />
                <Route exact path={ROUTES.PUBLIC.RANKINGS} component={PublicRankingsRoute} />
                <Route exact path={`${ROUTES.PUBLIC.RANKINGS}/:uuid`} component={PublicRankingRoute} />
                <Route exact path={`${ROUTES.PUBLIC.RANKINGS}/:uuid/image`} component={PublicRankingImageRoute} />
                <Route
                  exact
                  path={`${ROUTES.PUBLIC.ORGANIZATION}/:organizationSlug/members/:memberID`}
                  component={PublicMemberProfileRoute}
                />
                <Route
                  exact
                  path={`${ROUTES.PUBLIC.ORGANIZATION}/:organizationSlug`}
                  component={PublicOrganizationLandingRoute}
                />
                {/* render (not component) so the TV board is never remounted and keeps polling */}
                <Route
                  exact
                  path={`${ROUTES.PUBLIC.EVENT}/:eventUUID/board`}
                  render={(props) => (
                    <ErrorBoundary {...props}>
                      <AsyncClubBoard {...props} />
                    </ErrorBoundary>
                  )}
                />
                <Route exact path={`${ROUTES.PUBLIC.EVENT}/:eventUUID`} component={PublicEventDetailRoute} />

                {/* rutas admin protegidas */}
                <Route path="/admin">
                  <AdminRouteGroup isLoggedIn={userContext.loggedIn}>
                    <Route exact path={ROUTES.ADMIN.DASHBOARD_CALENDAR} render={redirectToDashboard} />
                    <Route exact path={ROUTES.ADMIN.DASHBOARD} component={HomeRoute} />
                    <Route path={`${ROUTES.ADMIN.EVENT}/create`} component={CreateCompetitionWizardRoute} />
                    <Route exact path={ROUTES.ADMIN.ORGANIZATIONS} component={OrganizationDashboardRoute} />
                    <Route exact path={ROUTES.ADMIN.GESTION} component={GestionCustomerRedirectRoute} />
                    <Route exact path={`${ROUTES.ADMIN.GESTION}/:customerId`} component={GestionDashboardRoute} />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/tournaments/create`}
                      component={GestionCreateTournamentRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/tournaments/:tournamentId`}
                      component={GestionTournamentRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/tournaments`}
                      component={GestionTournamentsRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/competitions`}
                      component={redirectToGestionTournaments}
                    />
                    <Route
                      path={`${GESTION_COMPETITION_BASE}/tournaments/create`}
                      component={GestionCreateCompetitionTournamentRoute}
                    />
                    <Route
                      path={`${GESTION_COMPETITION_BASE}/custom-fields`}
                      component={GestionCompetitionCustomFieldsRoute}
                    />
                    <Route
                      path={`${GESTION_COMPETITION_BASE}/scheduling`}
                      component={GestionCompetitionSchedulingRoute}
                    />
                    <Route path={`${GESTION_COMPETITION_BASE}/schedule`} component={GestionCompetitionScheduleRoute} />
                    <Route path={`${GESTION_COMPETITION_BASE}/edit`} component={GestionCompetitionEditRoute} />
                    <Route path={`${GESTION_COMPETITION_BASE}/clubs`} component={GestionCompetitionClubsRoute} />
                    <Route path={`${GESTION_COMPETITION_BASE}/managers`} component={GestionCompetitionManagersRoute} />
                    <Route
                      exact
                      path={`${GESTION_COMPETITION_BASE}/tournaments`}
                      component={redirectToGestionTournaments}
                    />
                    <Route exact path={GESTION_COMPETITION_BASE} component={redirectToGestionTournaments} />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/teams/:teamId/financials`}
                      component={GestionAccountFinanceRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/players/:memberId/financials`}
                      component={GestionAccountFinanceRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/financials/accounts/:entityId/:individualId?`}
                      component={GestionAccountFinanceRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/financials/payments/:paymentId`}
                      component={GestionPaymentDetailRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/teams/:teamId`}
                      component={GestionTeamDetailRoute}
                    />
                    <Route exact path={`${ROUTES.ADMIN.GESTION}/:customerId/teams`} component={GestionTeamsRoute} />
                    <Route exact path={`${ROUTES.ADMIN.GESTION}/:customerId/players`} component={GestionPlayersRoute} />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/memberships`}
                      component={redirectToGestionPlayers}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/membership-settings`}
                      component={redirectToGestionMembershipSettings}
                    />
                    <Route exact path={`${ROUTES.ADMIN.GESTION}/:customerId/matches`} component={GestionMatchesRoute} />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/venues`}
                      component={redirectToGestionSettings}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/branding`}
                      component={redirectToGestionSettingsApp}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/settings/app`}
                      component={GestionBrandingRoute}
                    />
                    <Route exact path={`${ROUTES.ADMIN.GESTION}/:customerId/settings`} component={GestionSettingsRoute} />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/financials`}
                      component={GestionFinancialsRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/notifications`}
                      component={GestionPushBroadcastsRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.GESTION}/:customerId/migration`}
                      component={GestionMigrationRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/membership-settings`}
                      component={OrganizationMembershipSettingsRoute}
                    />
                    <Route path={`${ROUTES.ADMIN.ORGANIZATIONS}/members`} component={OrganizationMembersRoute} />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/unlinked-players`}
                      component={OrganizationUnlinkedPlayersRoute}
                    />
                    <Route exact path={`${ROUTES.ADMIN.ORGANIZATIONS}/settings`} component={OrganizationSettingsRoute} />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classifications`}
                      component={OrganizationClassificationsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classifications/create`}
                      component={OrganizationCreateClassificationRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classifications/archived`}
                      component={OrganizationArchivedClassificationsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classifications/:uuid`}
                      component={OrganizationClassificationShowRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/punctuations`}
                      component={OrganizationPunctuationsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/punctuations/create`}
                      component={OrganizationCreatePunctuationRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/punctuations/:uuid/edit`}
                      component={OrganizationPunctuationShowRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classification-events`}
                      component={OrganizationClassificationEventsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classification-events/create`}
                      component={OrganizationCreateClassificationEventRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.ORGANIZATIONS}/classification-events/:uuid/edit`}
                      component={OrganizationEditClassificationEventRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.EVENT}/:id`}
                      component={CompetitionDashboardRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/clubs`}
                      component={CompetitionClubsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/managers`}
                      component={CompetitionManagersRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.EVENT}/:id/tournaments`}
                      component={CompetitionTournamentsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/tournaments/create`}
                      component={CreateCompetitionTournamentRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.EVENT}/:id/tournaments/:tournamentId`}
                      component={CompetitionTournamentRoute}
                    />
                    <Route
                      exact
                      path={`${ROUTES.ADMIN.EVENT}/:id/registrations`}
                      component={CompetitionRegistrationsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/edit`}
                      component={CompetitionEditRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/scheduling`}
                      component={CompetitionSchedulingRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/schedule`}
                      component={CompetitionScheduleRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/custom-fields`}
                      component={CompetitionCustomFieldsRoute}
                    />
                    <Route
                      path={`${ROUTES.ADMIN.EVENT}/:id/match-scoring`}
                      component={CompetitionMatchScoringRoute}
                    />

                    <Route exact path={ROUTES.ADMIN.STATS_BY_SPORT} component={StatsBySportRoute} />

                    {/* 404 Page */}
                    <Route component={ErrorPage} />
                  </AdminRouteGroup>
                </Route>

                {/* Error Routes */}
                <Route exact path="/500" component={GeneralErrorRoute} />
                <Route component={NotFoundRoute} />
              </Switch>
            </div>
          </Router>
        )}
      </React.Fragment>
    );
  }
}
export default withUserContext(AppRouter);
